'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useForm } from 'react-hook-form';

import { EMAIL_REGEX } from 'lib/constants';
import { fetchJson } from 'lib/fetch-json';
import Alert from 'src/components/Alert';
import Button from 'src/components/Button';
import FormError from 'src/components/FormError';

import useRecaptcha from 'src/hooks/useRecaptcha';

const AnimatedSmiley = dynamic(() => import('src/components/AnimatedSmiley'), {
    ssr: false,
});

export default function PageForm({ locations }) {
    const { isReady, generateToken } = useRecaptcha();
    const [errorMessage, setErrorMessage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const {
        register,
        formState: { errors: _errors },
        handleSubmit,
    } = useForm({
        defaultValues: {
            email: '',
            location: '',
            information: '',
            sampling: '',
            personnel: '',
            results: '',
            cleanliness: '',
            referral: '',
            reason: '',
            recommend: '',
        },
    });
    const errors: any = _errors;

    const onSubmit = async (params: any) => {
        setLoading(true);
        setErrorMessage(false);
        try {
            const recaptcha = await generateToken();

            await fetchJson(`/api/forms/satisfaction`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...params, recaptcha }),
            });

            setLoading(false);
            setSubmitted(true);
        } catch (error: any) {
            setLoading(false);
            setErrorMessage(true);
        }
    };

    return (
        <>
            {errorMessage && (
                <Alert type="danger" className="mb-3">
                    A apărut o eroare la trimiterea formularului. Vă rugăm să încercați mai târziu.
                </Alert>
            )}

            {!submitted && (
                <form
                    id="satisfaction-form"
                    className="my-4 flex flex-col gap-4 md:my-8"
                    noValidate
                    onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <div>
                            <label htmlFor="email">
                                Email<span className="required-indicator">*</span>
                            </label>
                            <input
                                type="email"
                                placeholder="Email*"
                                required
                                className={errors.email ? 'form-error' : ''}
                                id="email"
                                {...register('email', {
                                    required: 'Completarea adresei de email este obligatorie',
                                    pattern: {
                                        value: EMAIL_REGEX,
                                        message: 'Adresa de email este incorectă',
                                    },
                                })}
                            />
                            <FormError error={errors.email} />
                        </div>

                        <div>
                            <label htmlFor="location">
                                Laboratorul evaluat<span className="required-indicator">*</span>
                            </label>
                            <select
                                id="location"
                                className={errors.location ? 'form-error' : ''}
                                disabled={!locations?.length}
                                required
                                {...register('location', {
                                    required: 'Completarea locației este obligatorie',
                                })}>
                                <option value="" disabled={!locations?.length}>
                                    Selectați o locație
                                </option>
                                {locations?.map((location) => (
                                    <option key={location.slug} value={location.slug}>
                                        {location.name}, {location.city?.name}
                                    </option>
                                ))}
                            </select>
                            <FormError error={errors.location} />
                        </div>
                    </div>

                    <p className="my-4">
                        Pentru a cunoaște nevoile tale te rugăm să apreciezi calitatea serviciilor noastre alegând unul
                        din calificativele chestionarului:
                    </p>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <div>
                            <label htmlFor="information" className="control-label">
                                Informarea pacientului
                            </label>
                            <select id="information" {...register('information')}>
                                <option disabled value="">
                                    Te rugăm să selectezi
                                </option>
                                <option value="Foarte bine">Foarte bine</option>
                                <option value="Bine">Bine</option>
                                <option value="Satisfacator">Satisfăcător</option>
                                <option value="Slab">Slab</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="sampling">Recoltarea probelor</label>
                            <select id="sampling" {...register('sampling')}>
                                <option disabled value="">
                                    Te rugăm să selectezi
                                </option>
                                <option value="Foarte bine">Foarte bine</option>
                                <option value="Bine">Bine</option>
                                <option value="Satisfacator">Satisfăcător</option>
                                <option value="Slab">Slab</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="personnel">Conduita personalului</label>
                            <select id="personnel" {...register('personnel')}>
                                <option disabled value="">
                                    Te rugăm să selectezi
                                </option>
                                <option value="Foarte bine">Foarte bine</option>
                                <option value="Bine">Bine</option>
                                <option value="Satisfacator">Satisfăcător</option>
                                <option value="Slab">Slab</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="results">Eliberarea rezultatelor</label>
                            <select id="results" {...register('results')}>
                                <option disabled value="">
                                    Te rugăm să selectezi
                                </option>
                                <option value="Foarte bine">Foarte bine</option>
                                <option value="Bine">Bine</option>
                                <option value="Satisfacator">Satisfăcător</option>
                                <option value="Slab">Slab</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="cleanliness">Curățenia</label>
                            <select id="cleanliness" {...register('cleanliness')}>
                                <option disabled value="">
                                    Te rugăm să selectezi
                                </option>
                                <option value="Foarte bine">Foarte bine</option>
                                <option value="Bine">Bine</option>
                                <option value="Satisfacator">Satisfăcător</option>
                                <option value="Slab">Slab</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="referral">Cum ați aflat de noi?</label>
                            <select id="referral" {...register('referral')}>
                                <option disabled value="">
                                    Te rugăm să selectezi
                                </option>
                                <option value="Medic de familie">Medic de familie</option>
                                <option value="Medic de specialitate">Medic de specialitate</option>
                                <option value="Prieteni/familie">Prieteni/familie</option>
                                <option value="Facebook">Facebook</option>
                                <option value="Motor cautare">Motor de căutare (Google, Yahoo, etc.)</option>
                                <option value="Altundeva">Altundeva</option>
                            </select>
                        </div>

                        <div>
                            <label htmlFor="reason">De ce ați ales Bioclinica?</label>
                            <select id="reason" {...register('reason')}>
                                <option disabled value="">
                                    Te rugăm să selectezi
                                </option>
                                <option value="Promptitudine in eliberarea rezultatelor">
                                    Promptitudine în eliberarea rezultatelor
                                </option>
                                <option value="Preturi">Prețuri</option>
                                <option value="Gama variata de analize">Gamă variată de analize</option>
                                <option value="Recomandarea medicului curant">Recomandarea medicului curant</option>
                                <option value="Aparatura medicală performantă">Aparatura medicală performantă</option>
                                <option value="Altele">Altele</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <div className="flex flex-row gap-4">
                            <label htmlFor="recommend">
                                Ați recomanda Laboratoarele Bioclinica unui membru al familiei, prieten sau cunoscut?
                            </label>
                            <div>
                                <input id="recommend-yes" type="radio" value="Da" {...register('recommend')} />{' '}
                                <label htmlFor="recommend-yes">Da</label>
                            </div>
                            <div>
                                <input id="recommend-no" type="radio" value="Nu" {...register('recommend')} />{' '}
                                <label htmlFor="recommend-no">Nu</label>
                            </div>
                        </div>
                    </div>

                    <div className="text-black-50 my-5" style={{ fontSize: '0.9rem' }}>
                        Vă mulțumim pentru intenția de a ne contacta. Deoarece protecția datelor dumneavoastră cu
                        caracter personal reprezintă o prioritate pentru noi, vom folosi datele pe care ni le furnizați
                        doar pentru a vă contacta și pentru a vă oferi un răspuns la întrebarea dvs. Pentru mai multe
                        detalii despre modul în care vă tratăm datele, puteți verifica întotdeauna{' '}
                        <Link href="/protectia-datelor-cu-caracter-personal">
                            Politica noastră privind protecția datelor cu caracter personal
                        </Link>
                        .
                    </div>

                    <div>
                        <Button variant="secondary" type="submit" disabled={loading || !isReady}>
                            Trimite chestionarul
                        </Button>
                    </div>
                </form>
            )}

            {submitted && (
                <div className="py-4 text-center md:py-8">
                    <div className="text-light-green aspect-1 mx-auto w-3/4 md:w-1/2 lg:w-1/4">
                        <AnimatedSmiley variant="happy" />
                    </div>
                    <h2 className="mb-4 text-3xl md:text-4xl">Succes!</h2>
                    <p>
                        Mulțumim pentru feedback-ul dumneavoastră! Încercăm să vă răspundem în cel mai scurt timp
                        posibil.
                    </p>
                </div>
            )}
        </>
    );
}
